import * as React from 'react';
import { Button, Chip, IconButton, InputAdornment, Radio, Stack, TextField, debounce } from "@mui/material"
import Grid from "@mui/material/Grid2"
import { Box } from "@mui/system"
import { SimpleCustomToolbar } from "../CustomToolbar"
import CustomerSelection from '../CustomerSelection';
import { useTrackedDarkMode, useTrackedLocation, useTrackedUser } from '../UserState';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { AXIOS_API_INSTANCE, SERVICE_ORDER_CUSTOMER_APPROVAL_COLOR_MAP, all_locations_object, emailRegex } from "../../Constants"
import { send_authorized_request } from "../../Constants/cognito"
import { DataGrid } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import CustomerItemModal from './Modals/CustomerItemModal';
import HandymanIcon from '@mui/icons-material/Handyman';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingBackdropIndeterminate } from '../LoadingBackdrop';
import { moneyFormat, widthChangeEvent } from '../../Utils';
import Alert from '@mui/material/Alert';
import DocumentTitle from '../../Utils/DocumentTitle';
import { findServiceOrderItemType, useServiceOrderItemTypes } from '../ReactQuery/QueryServiceOrderItemTypes';
import { sort_custom_statuses } from '../../Utils/service';
import onFilterChange from '../../Utils/OnFilterChange';
import AddServiceOrderServiceModal from './Modals/AddServiceOrderServiceModal';

export default function NewServiceOrder() {
    DocumentTitle(`New Service Order`)
    const { enqueueSnackbar } = useSnackbar();
    const [selectedLocation,] = useTrackedLocation();
    const [my_user,] = useTrackedUser();
    const { data: serviceOrderItemTypes } = useServiceOrderItemTypes();
    const [darkMode,] = useTrackedDarkMode();
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: parseInt(localStorage.getItem(`customeritems:pageSize`) || 25),
        page: 0,
    });
    const [rowData, setRowData] = React.useState({ rows: [], total_count: 0 })
    const [sorting, setSorting] = React.useState({ field: 'manufacturer', sort: 'asc' })
    const [filtering, setFiltering] = React.useState({})
    const [isLoading, setIsLoading] = React.useState(false)
    const [serviceOrderCreatingStep, setServiceOrderCreatingStep] = React.useState(0)
    const [customerNotes, setCustomerNotes] = React.useState("")
    const [customerData, setCustomerData] = React.useState({
        customer: {},
        createNewCustomer: false,
        editingCustomer: false,
        editedCustomer: {}
    })
    const [preferredPhone, setPreferredPhone] = React.useState('')
    const [preferredEmail, setPreferredEmail] = React.useState('')
    const [customerItemModal, setCustomerItemModal] = React.useState({
        open: false,
        customerItem: {},
        creatingNewCustomerItem: false,
    })
    const [addServiceModalOpen, setAddServiceModalOpen] = React.useState(false);
    const [addedServiceKits, setAddedServiceKits] = React.useState([]);
    const [newCustomerItem, setNewCustomerItem] = React.useState(undefined)
    const [selectedCustomerItemId, setSelectedCustomerItemId] = React.useState(undefined)
    const [customerItemSearch, setCustomerItemSearch] = React.useState('')
    const [selectedServiceOrderItemTypeId, setSelectedServiceOrderItemTypeId] = React.useState(undefined)
    const [windowInnerWidth, setWindowInnerWidth] = React.useState(window.innerWidth)
    const navigate = useNavigate();
    const borderColor = darkMode ? '#FFF' : 'rgba(0, 0, 0, 0.87)'

    const columns = [
        {
            field: "radiobutton",
            type: "actions",
            headerName: "",
            width: 50,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Radio
                    checked={selectedCustomerItemId === params.id || newCustomerItem?.id === params.id}
                    value={params.id}
                    disabled={newCustomerItem?.id !== undefined && newCustomerItem?.id !== params.id}
                    onClick={() => {
                        setSelectedCustomerItemId(params.id)
                        setSelectedServiceOrderItemTypeId(params.row.service_order_item_type_id)
                    }}
                />
            )
        },
        {
            field: "manufacturer",
            headerName: "Manufacturer",
            flex: 1,
            editable: false,
            renderCell: (params) => {
                return (<span>{params.row.id === -1 && <b>(NEW) </b>}{params.value}</span>)
            }
        },
        {
            field: "model",
            headerName: "Model",
            flex: 1,
            editable: false,
        },
        {
            field: "identifier",
            headerName: "Identifier",
            flex: 1,
            editable: false,
        },
        {
            field: 'action',
            headerName: '',
            type: 'actions',
            width: 150,
            sortable: false,
            disableClickEventBubbling: true,

            renderCell: (params) => {
                return (
                    <Stack direction="row" spacing={1}>
                        <Button variant="contained" color="warning" size="small" onClick={() => editCustomerItemRowClick(params.row)}>Edit</Button>
                        {params.row.id === -1 && <Button variant="contained" color="error" size="small" onClick={cancelCreateCustomerItemClick}>Cancel</Button>}
                    </Stack>
                );
            },
        }
    ]
    const updateCustomerInfo = (update) => {
        if (update.customer.id !== customerData.customer.id) {
            debouncedGetCustomerItems(sorting.field, sorting.sort, paginationModel, filtering, customerItemSearch, update)
        }
        setCustomerData({ ...update })
        setNewCustomerItem(undefined)
        setSelectedCustomerItemId(undefined)
        setSelectedServiceOrderItemTypeId(undefined)
    }

    const cancelCreateCustomerItemClick = () => {
        setNewCustomerItem(undefined)
        setSelectedCustomerItemId(undefined)
        setSelectedServiceOrderItemTypeId(undefined)
    }
    const editCustomerItemRowClick = (row) => {
        setCustomerItemModal({
            customerItem: row,
            open: true,
            creatingNewCustomerItem: false,
        })
    }
    const createCustomerItemClick = () => {
        setCustomerItemModal({
            customerItem: {
                id: -1,
                identifier: '',
                model: '',
            },
            open: true,
            creatingNewCustomerItem: true,
        })
    }
    const closeCustomerItemModal = (changes) => {
        setCustomerItemModal({ ...customerItemModal, open: false })
        if (changes) {
            if (customerItemModal.creatingNewCustomerItem || changes.id === -1) {
                setNewCustomerItem(changes)
                setSelectedServiceOrderItemTypeId(changes.service_order_item_type_id)
                setSelectedCustomerItemId(undefined)
            } else {
                getCustomerItems(sorting.field, sorting.sort, paginationModel, filtering, customerItemSearch, customerData)
            }
        }
    }

    const setMyPaginationModel = (model) => {
        localStorage.setItem(`customeritems:pageSize`, model.pageSize)
        setPaginationModel(model)
    }
    const updateCustomerItemSearch = (event) => {
        setCustomerItemSearch(event.target.value.trim())
        if (event.target.value.trim() !== customerItemSearch) {
            debouncedGetCustomerItems(sorting.field, sorting.sort, paginationModel, filtering, event.target.value.trim(), customerData)
        }
    }

    const getCustomerItems = (sortField, sortDirection, paginationModel, filterVar, customerItemSearch, customerData) => {
        if (customerData?.customer?.id === undefined) {
            setRowData({
                rows: [],
                total_count: 0,
            })
            return
        }
        setIsLoading(true)
        let my_filters = Object.assign({}, filterVar);
        my_filters['customer_id.eq'] = customerData.customer.id
        if (customerItemSearch.length > 0) {
            my_filters['or'] = [
                { 'identifier.ilike': customerItemSearch },
                { 'manufacturer.ilike': customerItemSearch },
                { 'model.ilike': customerItemSearch },
            ]
        }
        send_authorized_request(() =>
            AXIOS_API_INSTANCE.get("customeritems", {
                params: {
                    page_num: paginationModel.page,
                    page_size: paginationModel.pageSize,
                    order_by: sortField,
                    order_by_direction: sortDirection,
                    filters: JSON.stringify(my_filters)
                },
            }).then((customerItems) => {
                setRowData({
                    rows: customerItems.data.data,
                    total_count: customerItems.data.total_count,
                })
                setIsLoading(false)
            }).catch((error) => {
                setIsLoading(false)
                enqueueSnackbar(`Failed to get customer items <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
            })
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedGetCustomerItems = React.useMemo(() => debounce((sortField, sortDirection, paginationModel, filterVar, customerItemSearch, customerData) => getCustomerItems(sortField, sortDirection, paginationModel, filterVar, customerItemSearch, customerData), 400), []);

    const save = () => {
        if (customerData.customer.id === undefined && !customerData.createNewCustomer) {
            enqueueSnackbar(`Customer is required`, { variant: 'error' })
            return
        }
        if (customerData.createNewCustomer) {
            if (Boolean(customerData.editedCustomer.name) === false && Boolean(customerData.editedCustomer.company_name) === false) {
                enqueueSnackbar(`Customer name is required`, { variant: 'error' })
                return
            }
        }
        if (customerNotes.length === 0) {
            enqueueSnackbar(`Customer Drop Off Notes are required`, { variant: 'error' })
            return
        }
        if (newCustomerItem === undefined && selectedCustomerItemId === undefined) {
            enqueueSnackbar(`Customer Item is required`, { variant: 'error' })
            return
        }
        if (selectedLocation.id === all_locations_object.id) {
            enqueueSnackbar(`Specific Location is required`, { variant: 'error' })
            return
        }
        if (!(/^\d{10}$/.test(preferredPhone) || preferredPhone.length === 0)) {
            enqueueSnackbar(`Invalid Preferred Contact Phone provided`, { variant: 'error' })
            return
        }
        if (!(Boolean(preferredEmail.toLowerCase().match(emailRegex)) || preferredEmail.length === 0)) {
            enqueueSnackbar(`Invalid Preferred Contact Email provided`, { variant: 'error' })
            return
        }
        // TODO: allow user to select this status, or define a default
        const status_name = sort_custom_statuses(my_user.client.service_order_statuses).find((key) => my_user.client.service_order_statuses[key].type === 'CREATED')
        const myServiceOrder = {
            customer_id: customerData.customer.id,
            create_new_customer: customerData.createNewCustomer,
            edit_customer: customerData.editingCustomer,
            customer: customerData.editedCustomer,
            create_new_customer_item: newCustomerItem !== undefined,
            customer_item: newCustomerItem,
            customer_item_id: selectedCustomerItemId,
            service_order_item_type_id: selectedServiceOrderItemTypeId,
            location_id: selectedLocation.id,
            customer_notes: customerNotes,
            status_name: status_name
        }
        if (preferredEmail.length > 0) {
            myServiceOrder.preferred_email = preferredEmail
        }
        if (preferredPhone.length > 0) {
            myServiceOrder.preferred_phone = preferredPhone
        }
        const includes_deposit = findServiceOrderItemType(serviceOrderItemTypes, selectedServiceOrderItemTypeId)?.deposit > 0
        setServiceOrderCreatingStep(1)
        send_authorized_request(() => AXIOS_API_INSTANCE.put(`/serviceorders`, myServiceOrder).then((serviceOrder) => {
            enqueueSnackbar('Successfully created service order', { variant: 'success' })
            localStorage.setItem('printTagCheckIn', 'true');
            const addedServiceKitRequests = addedServiceKits.map((new_service) =>
                AXIOS_API_INSTANCE.post(`serviceorders/${serviceOrder.data.data.id}/add_service`, new_service).then((serviceOrder) => {
                    enqueueSnackbar('Successfully added service', { variant: 'success' })
                }).catch((error) => {
                    enqueueSnackbar(`Failed to add service <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
                }))
            Promise.all(addedServiceKitRequests).finally(() => {
                setServiceOrderCreatingStep(0)
                setAddedServiceKits([])
                if (includes_deposit) {
                    navigate(`/ServiceOrders/${serviceOrder.data.data.id}#payment`)
                } else {
                    navigate(`/ServiceOrders/${serviceOrder.data.data.id}`)
                }
            })
        }).catch((error) => {
            setServiceOrderCreatingStep(0)
            enqueueSnackbar(`Failed to create service order <${error.response?.data?.message ?? "Unknown Error Occurred"}>`, { variant: 'error' })
        }))
    }

    React.useEffect(() => {
        return widthChangeEvent(setWindowInnerWidth)
    }, [])

    React.useEffect(() => {
        getCustomerItems(sorting.field, sorting.sort, paginationModel, filtering, customerItemSearch, customerData)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, sorting])

    React.useEffect(() => {
        debouncedGetCustomerItems(sorting.field, sorting.sort, paginationModel, filtering, customerItemSearch, customerData)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtering])

    const not_ready_to_submit = (selectedCustomerItemId || newCustomerItem?.id) === undefined || serviceOrderCreatingStep

    return (
        (<Box sx={{ margin: 'auto', marginTop: 0, display: 'block', maxWidth: '900px' }}>
            <LoadingBackdropIndeterminate
                open={serviceOrderCreatingStep > 0}
                text={"Creating Service Order"}
            />
            <Grid container direction="column" spacing={1} >
                {selectedLocation.id === all_locations_object.id &&
                    <Grid size="grow">
                        <Alert variant="filled" severity="error">
                            Please select a location
                        </Alert>
                    </Grid>
                }
                <Grid size="grow">
                    <CustomerSelection
                        autoFocus={true}
                        updateFunction={updateCustomerInfo}
                        customerData={customerData}
                        preferredEmail={preferredEmail}
                        preferredPhone={preferredPhone}
                        includePreferredContact={true}
                        updatePreferredContact={(type, value) => {
                            if (type === 'phone') {
                                if (value.nationalNumber !== null && value.nationalNumber !== undefined && value.nationalNumber.length < 11) {
                                    setPreferredPhone(value.nationalNumber)
                                }
                            } else if (type === 'email') {
                                setPreferredEmail(value)
                            }
                        }}
                    />
                </Grid>
                <Grid size="grow">
                    <h3>Customer Items</h3>
                    <Grid container sx={{ padding: "0" }} justifyContent={"space-between"} alignItems={"center"}>
                        <Grid>
                            <TextField
                                margin="dense"
                                id="customerItemSearch"
                                label="Search customer items"
                                type="text"
                                variant="filled"
                                disabled={isLoading}
                                value={customerItemSearch}
                                onChange={updateCustomerItemSearch}
                                slotProps={{
                                    input: {
                                        inputProps: { min: 1 },
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<SearchIcon />}
                                            </InputAdornment>
                                        ),
                                    }
                                }}
                            />
                        </Grid>
                        <Grid>
                            <Button onClick={createCustomerItemClick} disabled={newCustomerItem?.id !== undefined} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>New Customer Item</Box></Button>
                        </Grid>
                    </Grid>
                    <DataGrid
                        key={windowInnerWidth}
                        slots={{
                            toolbar: () => SimpleCustomToolbar(),
                        }}
                        rows={(newCustomerItem ? [newCustomerItem] : []).concat(rowData.rows)}
                        rowCount={rowData.total_count + (newCustomerItem ? 1 : 0)}
                        columns={columns}
                        experimentalFeatures={{ newEditingApi: true }}
                        autoHeight={true}
                        filterMode={"server"}
                        loading={isLoading}
                        rowSelectionModel={(selectedCustomerItemId || newCustomerItem?.id) ? [selectedCustomerItemId || newCustomerItem?.id] : []}
                        isRowSelectable={(params) => newCustomerItem?.id === undefined}
                        disableMultipleRowSelection={true}
                        onSortModelChange={(params) => {
                            if (params[0] !== undefined) {
                                setSorting(params[0])
                            } else {
                                setSorting({ field: 'id', sort: 'asc' })
                            }
                        }}
                        initialState={{
                            sorting: {
                                sortModel: [sorting],
                            },
                        }}
                        sortingMode="server"
                        onFilterModelChange={(params) => { setFiltering(onFilterChange(params)) }}
                        hideFooterSelectedRowCount
                        pageSizeOptions={[1, 5, 10, 25, 50, 100]}
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={setMyPaginationModel}
                    />
                </Grid>
                <Grid container columns={{ xs: 6, sm: 12 }} alignItems={'center'} sx={{ mt: 2 }} size="grow">
                    <Grid sx={{ textAlign: 'right' }} size={12}>
                        <b><span>Deposit: ${moneyFormat(findServiceOrderItemType(serviceOrderItemTypes, selectedServiceOrderItemTypeId)?.deposit)}</span></b>
                    </Grid>
                </Grid>
                <Grid container columns={{ xs: 4, sm: 8, md: 12 }} alignItems={'stretch'} sx={{ mt: 2 }} size="grow">
                    <Grid container size={12} spacing={3} alignItems="center">
                        <Grid size="auto">
                            <h2>Services {addedServiceKits.length ? `(${addedServiceKits.length})` : ''}</h2>
                        </Grid>
                        <Grid size="auto">
                            <Button onClick={() => setAddServiceModalOpen(true)}>
                                <AddCircleSharpIcon /> <Box sx={{ pl: 1 }}>Add Service</Box>
                            </Button>
                        </Grid>
                    </Grid>
                    {addedServiceKits.map((kit, index) => (
                        <Grid key={index} container direction={'column'} justifyContent={'space-between'} rowSpacing={0} size={4} sx={{ '&:hover': { transform: 'scale(1.01)' }, position: 'relative', border: `1px solid ${borderColor}`, borderRadius: '16px', padding: 1, minHeight: '100px', }}>
                            <Box sx={{ position: 'absolute', right: 0, top: 0 }}>
                                <IconButton onClick={() => { addedServiceKits.splice(index, 1); setAddedServiceKits([...addedServiceKits]) }} sx={{ color: 'red' }}><CloseIcon /></IconButton>
                            </Box>
                            <Grid size={12} sx={{ maxWidth: '100%' }}>
                                <h4 style={{ marginTop: 0, marginBottom: '8px', paddingRight: '30px' }}>{kit.name}</h4>
                            </Grid>
                            {kit.preapproved &&
                                <Grid size={12}>
                                    <Chip label={"Preapproved"} color={SERVICE_ORDER_CUSTOMER_APPROVAL_COLOR_MAP["APPROVED"]} />
                                </Grid>
                            }
                        </Grid>
                    ))}
                </Grid>
                <Grid container alignItems={'center'} rowSpacing={0} columnSpacing={3}>
                    <Grid size={12}>
                        <h3>Customer Drop Off Notes*</h3>
                    </Grid>
                    <Grid size={"grow"}>
                        <TextField
                            required
                            margin="dense"
                            id="customerdropoffnotes"
                            placeholder="Description of the problem"
                            type="text"
                            fullWidth
                            variant="filled"
                            multiline
                            minRows={3}
                            value={customerNotes}
                            onChange={(event) => setCustomerNotes(event.target.value)}
                            sx={{ mt: 0, pt: 0 }}
                            slotProps={{
                                input: { maxLength: 2055, style: { paddingTop: 12 } },
                                inputLabel: { shrink: false }
                            }} />
                    </Grid>
                    <Grid container size={"auto"} justifyContent={"flex-end"}>
                        <Grid>
                            <Button disabled={not_ready_to_submit} variant="contained" onClick={() => save()}><HandymanIcon /><Box sx={{ pl: 1 }}>Create Service Order</Box></Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <CustomerItemModal
                open={customerItemModal.open}
                customerItem={customerItemModal.customerItem}
                closeModal={closeCustomerItemModal}
                creatingNewCustomerItem={customerItemModal.creatingNewCustomerItem}
            />
            <AddServiceOrderServiceModal
                newServiceOrder={true}
                open={addServiceModalOpen}
                closeModal={(results) => {
                    setAddServiceModalOpen(false)
                    if (results) {
                        setAddedServiceKits([...addedServiceKits, results])
                    }
                }}
            />
        </Box>)
    );
}